import React, { useState } from 'react';
import { 
  CloudLightning, 
  Wind, 
  Droplets, 
  Mountain, 
  ThermometerSun,
  Lock,
  Flame,
  Ship
} from 'lucide-react';
import { useWeather } from '../context';
import { useAuth } from '../hooks/useAuth';

interface CatastropheType {
  id: string;
  name: string;
  icon: React.ReactNode;
  description: string;
  externalUrl?: string;
  modelConfig?: {
    model: string;
    parameter?: string;
  };
  beta?: boolean;
  restricted?: boolean;
}

export function CatastropheSelector() {
  const { setModelSelect, setParameterSelect } = useWeather();
  const { user } = useAuth();
  const [showSelector, setShowSelector] = useState(true);

  // Check if user has access to marine features
  const hasMarineAccess = user?.email === 'simplyben@me.com';

  const catastrophes: CatastropheType[] = [
    {
      id: 'extreme-weather',
      name: 'Extreme Weather',
      icon: <ThermometerSun className="w-6 h-6" />,
      description: 'Monitor heat waves, cold snaps, and severe weather',
      modelConfig: {
        model: 'openmeteo',
        parameter: 'temperature_2m_max'
      }
    },
    {
      id: 'severe-storms',
      name: 'Severe Convective Storms',
      icon: <CloudLightning className="w-6 h-6" />,
      description: 'Explore thunderstorms, tornadoes, and hail events',
      modelConfig: {
        model: 'xweather',
        parameter: 'storm_reports'
      }
    },
    {
      id: 'shipping',
      name: 'Shipping & Marine',
      icon: <Ship className="w-6 h-6" />,
      description: 'Track vessels and monitor marine weather risks',
      modelConfig: {
        model: 'shipping',
        parameter: 'tropicalcyclones'
      },
      beta: true,
      restricted: true
    },
    {
      id: 'hurricanes',
      name: 'Hurricanes & Windstorms',
      icon: <Wind className="w-6 h-6" />,
      description: 'Track tropical cyclones and coastal storms',
      externalUrl: 'https://demo.catxre.com/dashboard/explorer/windstorm/'
    },
    {
      id: 'drought',
      name: 'Drought',
      icon: <Droplets className="w-6 h-6" />,
      description: 'Monitor extended periods of water deficit',
      modelConfig: {
        model: 'drought'
      }
    },
    {
      id: 'earthquakes',
      name: 'Earthquakes',
      icon: <Mountain className="w-6 h-6" />,
      description: 'Track seismic activity and ground movement',
      externalUrl: 'https://demo.catxre.com/dashboard/explorer/earthquake/'
    },
    {
      id: 'wildfires',
      name: 'Wildfires (Realtime)',
      icon: <Flame className="w-6 h-6" />,
      description: 'Monitor active wildfires and fire risk',
      externalUrl: 'https://wildfire-app.trycatamaran.com/'
    }
  ];

  const handleSelect = (catastrophe: CatastropheType) => {
    if (catastrophe.restricted && !hasMarineAccess) {
      alert('You do not have access to this feature. Please contact support for more information.');
      return;
    }

    if (catastrophe.externalUrl) {
      window.open(catastrophe.externalUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    if (catastrophe.modelConfig) {
      setModelSelect(catastrophe.modelConfig.model);
      // Only set parameter if it exists
      if (catastrophe.modelConfig.parameter) {
        setParameterSelect(catastrophe.modelConfig.parameter);
      }
      setShowSelector(false);
    }
  };

  if (!showSelector) return null;

  // Filter out restricted options if user doesn't have access
  const availableCatastrophes = catastrophes.filter(cat => 
    !cat.restricted || hasMarineAccess
  );

  return (
    <div className="fixed inset-0 bg-[var(--primary-bg)] bg-opacity-90 flex items-center justify-center z-50">
      <div className="max-w-2xl w-full mx-4">
        <h1 className="text-2xl font-bold text-center mb-1">Weather Explorer</h1>
        <p className="text-sm text-[var(--secondary-text)] text-center mb-4">
          Select a catastrophe type to begin exploring
        </p>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {availableCatastrophes.map((cat) => (
            <button
              key={cat.id}
              onClick={() => handleSelect(cat)}
              className="glass-panel p-4 text-left hover:bg-[var(--accent-bg)] transition-all duration-200 relative group"
            >
              <div className="flex items-center gap-3">
                <div className="text-[var(--accent-color)] group-hover:scale-110 transition-transform">
                  {cat.icon}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="text-base font-semibold truncate">{cat.name}</h3>
                    {cat.externalUrl && (
                      <Lock className="w-3 h-3 text-[var(--secondary-text)] flex-shrink-0" />
                    )}
                    {cat.beta && (
                      <span className="text-xs bg-[var(--accent-color)] text-white px-1.5 py-0.5 rounded">
                        BETA
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-[var(--secondary-text)] line-clamp-2">
                    {cat.description}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}